import { useCallback, useMemo } from "react"
import { useSelector } from "react-redux"
import {
  canAdminAllViewModules,
  canAdminPermission,
  canAccessAdminRoute,
  canSeeAdminNavItem,
  getDefaultAdminPath,
  normalizeAdminPermissions,
} from "../helpers/adminPermissions"

/**
 * Admin portal permission hook.
 * super_admin → all allowed; admin staff → role permissions from /me.
 */
const useAdminPermission = () => {
  const currentUser = useSelector(state => state.auth.currentUser)
  const adminMeLoaded = useSelector(state => state.auth.adminMeLoaded)

  const permissionState = useMemo(
    () => normalizeAdminPermissions(currentUser),
    [currentUser],
  )

  const isReady = adminMeLoaded && permissionState.isReady

  const can = useCallback(
    (module, action = "view") =>
      canAdminPermission(permissionState, module, action),
    [permissionState],
  )

  const canAny = useCallback(
    (modules, action = "view") => {
      if (!Array.isArray(modules) || modules.length === 0) return false
      return modules.some(module =>
        canAdminPermission(permissionState, module, action),
      )
    },
    [permissionState],
  )

  const canViewAll = useCallback(
    (modules = []) => canAdminAllViewModules(permissionState, modules),
    [permissionState],
  )

  const canNav = useCallback(
    navKey => canSeeAdminNavItem(permissionState, navKey),
    [permissionState],
  )

  const canRoute = useCallback(
    pathname => canAccessAdminRoute(permissionState, pathname),
    [permissionState],
  )

  const defaultAdminPath = useMemo(
    () => getDefaultAdminPath(permissionState),
    [permissionState],
  )

  return {
    can,
    canAny,
    canViewAll,
    canNav,
    canRoute,
    defaultAdminPath,
    isReady,
    isSuperAdmin: permissionState.isSuperAdmin,
    isAdminStaff: permissionState.isAdminStaff,
    grants: permissionState.grants,
    permissionState,
  }
}

export default useAdminPermission
